import React, { useState, useEffect } from 'react';

const GSTCalculator = () => {
  const [amount, setAmount] = useState('');
  const [gstRate, setGstRate] = useState('18');
  const [includeGST, setIncludeGST] = useState(false);
  const [businessType, setBusinessType] = useState('regular');
  const [itcAvailable, setItcAvailable] = useState(false);
  const [itcPercentage, setItcPercentage] = useState('100');
  
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
      calculateGST();
    } else {
      setResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, gstRate, includeGST, itcAvailable, itcPercentage]);

  const calculateGST = () => {
    const baseAmount = parseFloat(amount);
    const rate = parseFloat(gstRate);
    const itcPercent = parseFloat(itcPercentage) / 100;

    let gstAmount, netAmount, totalAmount, cgst, sgst, igst;

    if (includeGST) {
      // Amount includes GST - extract GST
      totalAmount = baseAmount;
      gstAmount = (baseAmount * rate) / (100 + rate);
      netAmount = baseAmount - gstAmount;
    } else {
      // Amount excludes GST - add GST
      netAmount = baseAmount;
      gstAmount = (baseAmount * rate) / 100;
      totalAmount = baseAmount + gstAmount;
    }

    // Calculate CGST and SGST (split equally for intra-state)
    cgst = gstAmount / 2;
    sgst = gstAmount / 2;
    igst = gstAmount; // For inter-state

    // Calculate ITC (Input Tax Credit)
    let itcAmount = 0;
    let effectiveGST = gstAmount;
    
    if (itcAvailable) {
      itcAmount = gstAmount * itcPercent;
      effectiveGST = gstAmount - itcAmount;
    }

    // Calculate reverse charge if applicable
    let reverseCharge = 0;
    if (businessType === 'unregistered') {
      reverseCharge = gstAmount;
    }

    setResults({
      netAmount: netAmount.toFixed(2),
      gstAmount: gstAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      cgst: cgst.toFixed(2),
      sgst: sgst.toFixed(2),
      igst: igst.toFixed(2),
      itcAmount: itcAmount.toFixed(2),
      effectiveGST: effectiveGST.toFixed(2),
      reverseCharge: reverseCharge.toFixed(2),
      effectiveRate: ((effectiveGST / netAmount) * 100).toFixed(2),
    });
  };

  const copyToClipboard = () => {
    if (!results) return;
    
    const summary = `
📊 GST Calculation Summary
━━━━━━━━━━━━━━━━━━━━━━
${includeGST ? 'Amount (Inc. GST)' : 'Base Amount'}: ₹${amount}
GST Rate: ${gstRate}%
━━━━━━━━━━━━━━━━━━━━━━
Base Amount: ₹${results.netAmount}
GST Amount: ₹${results.gstAmount}
${itcAvailable ? `ITC Available: ₹${results.itcAmount}` : ''}
${itcAvailable ? `Effective GST: ₹${results.effectiveGST}` : ''}
Total Amount: ₹${results.totalAmount}
━━━━━━━━━━━━━━━━━━━━━━
CGST: ₹${results.cgst}
SGST: ₹${results.sgst}
IGST: ₹${results.igst}
${itcAvailable ? `\nEffective Rate: ${results.effectiveRate}%` : ''}
━━━━━━━━━━━━━━━━━━━━━━
Generated via Vyqda.com/tools
    `;
    
    navigator.clipboard.writeText(summary.trim());
    alert('✅ GST summary copied to clipboard!');
  };

  const shareOnWhatsApp = () => {
    if (!results) return;
    
    const text = `📊 GST Calculation: Base ₹${results.netAmount} + GST ₹${results.gstAmount} = Total ₹${results.totalAmount}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Input Section */}
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">🧾</span> GST Calculator
        </h2>

        <div className="space-y-5">
          {/* Amount Input */}
          <div>
            <label className="label">
              {includeGST ? 'Amount (Including GST)' : 'Base Amount (Excluding GST)'}
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="input-field pl-8"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* GST Rate */}
          <div>
            <label className="label">GST Rate</label>
            <select
              value={gstRate}
              onChange={(e) => setGstRate(e.target.value)}
              className="input-field"
            >
              <option value="0">0% - Nil Rated</option>
              <option value="0.25">0.25% - Rough Precious Stones</option>
              <option value="3">3% - Gold, Silver</option>
              <option value="5">5% - Essential Items</option>
              <option value="12">12% - Standard Items</option>
              <option value="18">18% - Most Services</option>
              <option value="28">28% - Luxury Items</option>
            </select>
          </div>

          {/* Include/Exclude GST Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="font-semibold text-gray-700">
              {includeGST ? '✓ Amount includes GST' : '✗ Amount excludes GST'}
            </span>
            <button
              onClick={() => setIncludeGST(!includeGST)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                includeGST ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  includeGST ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Business Type */}
          <div>
            <label className="label">Business Type</label>
            <select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              className="input-field"
            >
              <option value="regular">Regular GST Registered</option>
              <option value="composition">Composition Scheme</option>
              <option value="unregistered">Unregistered (Reverse Charge)</option>
            </select>
          </div>

          {/* ITC Available Toggle */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <span className="font-semibold text-gray-700 block">Input Tax Credit (ITC)</span>
              <span className="text-xs text-gray-500">Claim GST paid on purchases</span>
            </div>
            <button
              onClick={() => setItcAvailable(!itcAvailable)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                itcAvailable ? 'bg-success-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  itcAvailable ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* ITC Percentage */}
          {itcAvailable && (
            <div className="animate-slide-up">
              <label className="label">ITC Available (%)</label>
              <input
                type="number"
                value={itcPercentage}
                onChange={(e) => setItcPercentage(e.target.value)}
                placeholder="100"
                className="input-field"
                min="0"
                max="100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Usually 100% for business expenses, partial for specific items
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="space-y-6">
        {results ? (
          <>
            <div className="card">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📊</span> GST Breakdown
              </h3>
              
              <div className="space-y-4">
                <div className="result-card">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Base Amount</span>
                    <span className="text-2xl font-bold text-gray-900">₹{results.netAmount}</span>
                  </div>
                </div>

                <div className="result-card">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">GST Amount ({gstRate}%)</span>
                    <span className="text-2xl font-bold text-primary-600">₹{results.gstAmount}</span>
                  </div>
                  <div className="pt-2 border-t border-primary-200 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">CGST ({gstRate/2}%)</span>
                      <span className="font-semibold">₹{results.cgst}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">SGST ({gstRate/2}%)</span>
                      <span className="font-semibold">₹{results.sgst}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>or IGST (Inter-state)</span>
                      <span className="font-semibold">₹{results.igst}</span>
                    </div>
                  </div>
                </div>

                {itcAvailable && (
                  <div className="result-card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Input Tax Credit</span>
                      <span className="text-2xl font-bold text-success-600">-₹{results.itcAmount}</span>
                    </div>
                    <div className="pt-2 border-t border-green-200 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Effective GST Payable</span>
                        <span className="font-semibold text-success-600">₹{results.effectiveGST}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Effective Rate</span>
                        <span className="font-semibold text-success-600">{results.effectiveRate}%</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="result-card bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-semibold">Total Amount</span>
                    <span className="text-3xl font-bold text-indigo-600">₹{results.totalAmount}</span>
                  </div>
                </div>

                {businessType === 'unregistered' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>⚠️ Reverse Charge:</strong> As an unregistered supplier, the recipient will pay GST of ₹{results.reverseCharge}
                    </p>
                  </div>
                )}

                {businessType === 'composition' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Composition Scheme:</strong> Flat GST rate applicable. Cannot claim ITC.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="card">
              <h4 className="font-semibold text-gray-700 mb-3">Quick Actions</h4>
              <div className="flex flex-wrap gap-3">
                <button onClick={copyToClipboard} className="btn-primary flex items-center">
                  <span className="mr-2">📋</span> Copy Summary
                </button>
                <button onClick={shareOnWhatsApp} className="btn-secondary flex items-center">
                  <span className="mr-2">📱</span> Share on WhatsApp
                </button>
                <button onClick={() => window.print()} className="btn-secondary flex items-center no-print">
                  <span className="mr-2">🖨️</span> Print
                </button>
              </div>
            </div>

            {/* Pro Tips */}
            <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
              <h4 className="font-semibold text-amber-900 mb-2 flex items-center">
                <span className="mr-2">💡</span> Pro Tips
              </h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• File GSTR-1 by 11th of next month</li>
                <li>• File GSTR-3B by 20th of next month</li>
                <li>• Claim ITC only from GSTR-2B</li>
                <li>• Maintain proper invoices for ITC claims</li>
                <li>• E-way bill needed for goods above ₹50,000</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">🧮</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Calculate</h3>
            <p className="text-gray-500">Enter an amount to see GST breakdown</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GSTCalculator;

